import React, { useEffect, useState } from "react";
import { Image, Skeleton, Tooltip } from "antd";
import { FileImageOutlined } from "@ant-design/icons";
import { axiosInstance } from "@/utilities";
import { API_URL_PIM_PRODUCTS } from "@/services/XCONS_ENV";

/** Gris de 1px: lo muestra antd cuando la URL no resuelve, en vez del ícono roto. */
const PLACEHOLDER =
  "data:image/gif;base64,R0lGODlhAQABAIAAAOvr6wAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==";

/**
 * La miniatura de una imagen del catálogo.
 *
 * **Dos caminos, y el orden importa.** Si el API pudo armar la URL pública —lo hace con
 * `web/secure/base_media_url`— se usa esa: es un `<img>` directo, lo cachea el browser y
 * no pasa por el API. Esa config es **global** y el entorno local **no la tiene**, así que
 * ahí se cae al segundo camino: pedirle los bytes al API, que los lee de S3 con las
 * credenciales que ya usa para subirlos.
 *
 * El segundo no puede ser un `src` a secas porque un `<img>` no manda el header
 * `Authorization`, y abrir el endpoint sin auth expondría el bucket entero por un query
 * param. Por eso se pide con axios y se muestra como blob.
 */
export const MiniaturaMedia: React.FC<{
  file: string;
  url?: string;
  /** El lado en px. 48 en la lista del form; la ficha las muestra más grandes. */
  size?: number;
}> = ({ file, url, size = 48 }) => {
  const [blob, setBlob] = useState<string | null>(null);
  const [falla, setFalla] = useState(false);

  useEffect(() => {
    // Un archivo nuevo arranca de cero: sin esto, el error o el blob del anterior quedaban
    // pegados a la miniatura.
    setBlob(null);
    setFalla(false);
    if (url || !file) return;

    let vivo = true;
    let creada: string | null = null;

    axiosInstance
      .get(`${API_URL_PIM_PRODUCTS}/media/file`, {
        params: { path: file },
        responseType: "blob",
      })
      .then(({ data }) => {
        if (!vivo) return;
        creada = URL.createObjectURL(data as Blob);
        setBlob(creada);
      })
      .catch(() => vivo && setFalla(true));

    return () => {
      vivo = false;
      // Sin esto el blob queda en memoria hasta que se recargue la página, y el form se
      // monta de nuevo en cada navegación.
      if (creada) URL.revokeObjectURL(creada);
    };
  }, [file, url]);

  const src = url || blob;

  if (falla) {
    return (
      <Tooltip title="No se pudo leer el archivo de S3">
        <FileImageOutlined style={{ fontSize: size / 2, color: "#bfbfbf" }} />
      </Tooltip>
    );
  }

  if (!src)
    return <Skeleton.Image active style={{ width: size, height: size }} />;

  return (
    <Image
      src={src}
      alt={file}
      width={size}
      height={size}
      style={{ objectFit: "contain", background: "#fafafa" }}
      fallback={PLACEHOLDER}
    />
  );
};
